import type { ComuneFull } from '@/data';

// Агрегированные показатели района (SLL / comunità comprensoriale).
// Район — это объединение ЦЕЛЫХ коммун, поэтому все цифры выводятся из
// per-comune данных, новых данных собирать не нужно:
//   • extensive (население, иностранцы) — сумма;
//   • intensive (температура, интернет, доход, %) — среднее, взвешенное по
//     населению (большой город не тонет в десятках мелких коммун);
//   • плотность — суммарное население / суммарная площадь.

export interface DistrictAggregate {
  nComuni: number;
  pop: number;
  alt: number | null;
  climate: {
    t_mean: number | null;
    t_jul: number | null;
    t_jan: number | null;
    hot_days: number | null;
    precip: number | null;
    hum: number | null;
    snow: number | null;
    sunny: number | null;
    sun: number | null;
  };
  infra: {
    hosp: number | null;
    pharm: number | null;
    school: number | null;
    train: number | null;
    air: number | null;
    super: number | null;
    ski: number | null;
  };
  internet: { dl: number | null; ul: number | null; ping: number | null };
  density: number | null;
  foreign: {
    total: number;
    ru: number;
    ua: number;
    by: number;
    ru_pct: number | null;
    ua_pct: number | null;
    by_pct: number | null;
  } | null;
  econ: { rent_eur_m2y: number | null; sale_eur_m2: number | null; income_eur: number | null } | null;
  tax: { irpef_pct: number | null } | null;
  // Самая населённая коммуна — представитель района (для фото/контекста).
  topComune: ComuneFull | null;
  // Список членов, отсортированный по убыванию населения.
  members: ComuneFull[];
}

// Среднее, взвешенное по населению. Игнорирует записи с null-значением.
function wmean(members: ComuneFull[], get: (c: ComuneFull) => number | null | undefined): number | null {
  let wsum = 0;
  let vwsum = 0;
  let plainSum = 0;
  let count = 0;
  for (const c of members) {
    const v = get(c);
    if (v == null || !isFinite(v)) continue;
    const w = c.pop > 0 ? c.pop : 0;
    wsum += w;
    vwsum += v * w;
    plainSum += v;
    count++;
  }
  if (count === 0) return null;
  return wsum > 0 ? vwsum / wsum : plainSum / count;
}

function sum(members: ComuneFull[], get: (c: ComuneFull) => number | null | undefined): number {
  let s = 0;
  for (const c of members) {
    const v = get(c);
    if (v != null && isFinite(v)) s += v;
  }
  return s;
}

export function aggregateDistrict(members: ComuneFull[]): DistrictAggregate | null {
  if (members.length === 0) return null;
  const sorted = [...members].sort((a, b) => b.pop - a.pop);
  const pop = sum(members, (c) => c.pop);

  // density = Σpop / Σarea, area_i = pop_i / density_i
  let area = 0;
  for (const c of members) {
    const d = c.demo?.density;
    if (d != null && d > 0 && c.pop > 0) area += c.pop / d;
  }
  const density = area > 0 ? pop / area : wmean(members, (c) => c.demo?.density);

  const fTotal = sum(members, (c) => c.foreign?.total ?? null);
  const foreign =
    fTotal > 0
      ? {
          total: fTotal,
          ru: sum(members, (c) => c.foreign?.ru ?? null),
          ua: sum(members, (c) => c.foreign?.ua ?? null),
          by: sum(members, (c) => c.foreign?.by ?? null),
          ru_pct: pop > 0 ? (sum(members, (c) => c.foreign?.ru ?? null) / pop) * 100 : null,
          ua_pct: pop > 0 ? (sum(members, (c) => c.foreign?.ua ?? null) / pop) * 100 : null,
          by_pct: pop > 0 ? (sum(members, (c) => c.foreign?.by ?? null) / pop) * 100 : null,
        }
      : null;

  const rent = wmean(members, (c) => c.econ?.rent_eur_m2y ?? null);
  const sale = wmean(members, (c) => c.econ?.sale_eur_m2 ?? null);
  const income = wmean(members, (c) => c.econ?.income_eur ?? null);
  const econ = rent != null || sale != null || income != null
    ? { rent_eur_m2y: rent, sale_eur_m2: sale, income_eur: income }
    : null;

  const irpef = wmean(members, (c) => c.tax?.irpef_pct ?? null);

  return {
    nComuni: members.length,
    pop,
    alt: wmean(members, (c) => c.alt),
    climate: {
      t_mean: wmean(members, (c) => c.climate.t_mean),
      t_jul: wmean(members, (c) => c.climate.t_jul),
      t_jan: wmean(members, (c) => c.climate.t_jan),
      hot_days: wmean(members, (c) => c.climate.hot_days),
      precip: wmean(members, (c) => c.climate.precip),
      hum: wmean(members, (c) => c.climate.hum),
      snow: wmean(members, (c) => c.climate.snow),
      sunny: wmean(members, (c) => c.climate.sunny),
      sun: wmean(members, (c) => c.climate.sun),
    },
    infra: {
      hosp: wmean(members, (c) => c.infra.hosp),
      pharm: wmean(members, (c) => c.infra.pharm),
      school: wmean(members, (c) => c.infra.school),
      train: wmean(members, (c) => c.infra.train),
      air: wmean(members, (c) => c.infra.air),
      super: wmean(members, (c) => c.infra.super),
      ski: wmean(members, (c) => c.infra.ski),
    },
    internet: {
      dl: wmean(members, (c) => c.internet?.dl),
      ul: wmean(members, (c) => c.internet?.ul),
      ping: wmean(members, (c) => c.internet?.ping),
    },
    density,
    foreign,
    econ,
    tax: irpef != null ? { irpef_pct: irpef } : null,
    topComune: sorted[0] ?? null,
    members: sorted,
  };
}
